import { createFileRoute, Outlet, SearchSchemaInput, useNavigate, useRouter } from '@tanstack/react-router'
import backIcon from "@/assets/paytm/back.png"
import walletIcon from "@/assets/paytm/wallet.png"
import { useMutation, useQuery } from '@tanstack/react-query';
import { Api } from '@/lib/utils';
import { useStore } from '@/store';
import { useShallow } from 'zustand/shallow';
import PickupDropInput from '@/components/features/paytm/pickup-drop-input';
import { Address, LocationAddress, OutletDetails, Quote } from '@/lib/interfaces';
import { useEffect, useReducer } from 'react';
import Button from '@/components/features/paytm/button';
import { toast } from 'sonner';
import LoadingDeliveryOptions from '@/components/features/paytm/loading-delivery-options';

export const Route = createFileRoute('/paytm/neworder')({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown> & SearchSchemaInput): {
        address: LocationAddress,
        contact: string | undefined,
        storeId: string | undefined,
        addressType: "store" | "customer" | undefined
    } => {
        return {
            address: search.address as LocationAddress || undefined,
            contact: search.contact as string || undefined,
            storeId: search.storeId as string | undefined,
            addressType: search.addressType as "store" | "customer" || undefined
        }
    }
})

interface State {
    storeContact: string
    pickupAddress: LocationAddress | undefined
    dropLocation: LocationAddress | undefined
    customerContact: string
    storeId: string | undefined
    showDeliveryOptions: boolean
    showOrderDetails: boolean
    pickupOtp: string
    dropOtp: string
    searchLocation: boolean
    addLocation: boolean
    showWallet: boolean
    addressType: 'customer' | 'store' | undefined
}

const initialValue: State = {
    storeContact: '', customerContact: '', pickupAddress: undefined, showDeliveryOptions: false, searchLocation: false, showWallet: false,
    dropLocation: undefined, storeId: undefined, showOrderDetails: false, pickupOtp: '', dropOtp: '', addLocation: false, addressType: undefined
}

const reducer = (state: State, action: { type: 'reset', payload: Partial<State> } | { type: 'update', payload: Partial<State> }) => {
    switch (action.type) {
        case "update":
            return { ...state, ...action.payload }
        case "reset":
            return { ...initialValue, ...action.payload }
    }
}

function RouteComponent() {
    const { address, storeId, addressType, contact } = Route.useSearch()
    const router = useRouter();
    const navigate = useNavigate()
    const [state, dispatch] = useReducer(reducer, initialValue)
    const { loggedIn, token, phone, setPaytmTab } = useStore(useShallow(state => ({
        loggedIn: state.loggedIn,
        token: state.token,
        phone: state.phoneNumber,
        setPaytmTab: state.setPaytmTab
    })))

    const getWalletBalance = useQuery({
        queryKey: ['billing_info', token],
        queryFn: () => Api<{ status: number, enforce_credit_limit: boolean, wallet_balance: number | undefined, wallet_status: number | undefined }>('/webui/wallet_balance', "POST", {}, { token }),
        staleTime: 5 * 60 * 1000,
        refetchInterval: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: loggedIn
    })

    const getPickupList = useQuery({
        queryKey: ['pickupList', token],
        queryFn: () => Api<OutletDetails[]>('/webui/pickup/list', "POST", {}, { token }),
        staleTime: Infinity,
        refetchInterval: 15 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: loggedIn
    })

    useEffect(() => {
        setPaytmTab('Home')
    }, [])

    useEffect(() => {
        if (phone && (!state.storeContact || (state.storeContact === phone && !state.pickupAddress))) {
            dispatch({ type: 'update', payload: { storeContact: phone } })
            const store = getPickupList.data?.find(s => s.phone === phone)
            if (store) {
                dispatch({
                    type: 'update', payload: {
                        pickupAddress: {
                            lat: store.latitude, lng: store.longitude,
                            address: store.address, pincode: store.pincode, phone: store.phone, placeId: store.googlePlaceId
                        }, storeId: store.storeId
                    }
                })
            }
        }
    }, [getPickupList.data])

    useEffect(() => {
        if (address && contact && (storeId || addressType === 'customer')) {
            if (addressType === 'store') {
                dispatch({
                    type: 'update', payload: { pickupAddress: address, storeId: storeId, storeContact: contact }
                })
            } else {
                dispatch({
                    type: 'update', payload: { dropLocation: address, customerContact: contact }
                })
            }
        }
    }, [address, storeId, addressType, contact])

    const getCustomerInfo = useMutation({
        mutationFn: (phone: string) => {
            return Api<{
                status: number, customer: {
                    lat: number,
                    lng: number,
                    address: Address,
                    pincode: string
                    phone: string
                }
            }>('/webui/customer/info', "POST", { phone_number: phone }, { token })
        },
        onSuccess(data, _variables, _context) {
            if (data.status === 1) {
                dispatch({
                    type: 'update', payload: {
                        dropLocation: data.customer
                    }
                })
            }
        },
    })

    const getPriceQuotes = useMutation({
        mutationFn: (data: {
            storeDetails: { lat: number, lng: number, pin: string, city: string },
            drop: { lat: number, lng: number, pin: string }
        }) => {
            return Api<{ status: number, valid_until: string, quote_id: string, quotes: Quote[] }>('/webui/quotes', "POST",
                {
                    pickup: {
                        lat: data.storeDetails.lat,
                        lng: data.storeDetails.lng,
                        pincode: data.storeDetails.pin
                    },
                    drop: {
                        lat: data.drop.lat,
                        lng: data.drop.lng,
                        pincode: data.drop.pin
                    },
                    city: data.storeDetails.city,
                    order_amount: 2000
                }, { token })
        },
        onError(error) {
            toast.error(error.message ||  "Error finding delivery partner")
        },
        onSuccess(data) {
            if(state.storeId && state.dropLocation) {
                navigate({to: '/paytm/neworder/delivery-options', search: {
                    rechargeWallet: !getWalletBalance.data?.wallet_balance || getWalletBalance.data.wallet_balance < Math.max(...data.quotes.map(e => e.price_forward)),
                    quotes: data.quotes,
                    walletBalance: getWalletBalance.data?.wallet_balance || 0,
                    storeId: state.storeId,
                    customerLocation: state.dropLocation
                }})
            }
        }
    })

    return <div>
        <div className='absolute top-0 bottom-20 left-0 right-0'>
            <div className="h-[56px] flex justify-between px-[18px] items-center bg-light-blue">
                <div className="flex items-center">
                    <img src={backIcon} className="w-[20px] mr-[16px]" onClick={() => router.history.back()} />
                    <p className="font-bold text-lg">Paytm Swift</p>
                </div>
                <div className="flex items-center">
                    <img src={walletIcon} className="w-[32px] mr-[16px]" />
                    {getWalletBalance.data?.wallet_balance !== undefined ? <p className="font-semibold text-sm">{`₹ ${getWalletBalance.data.wallet_balance > 0 ? getWalletBalance.data?.wallet_balance.toFixed(2) : 0}`}</p> : null}
                </div>
            </div>
            <PickupDropInput onStorePhoneUpdate={val => {
                let store: OutletDetails | undefined
                if (!val || /^\d{0,10}$/.test(val)) {
                    dispatch({ type: 'update', payload: { storeContact: val, pickupAddress: val.length < 10 ? undefined : state.pickupAddress, storeId: undefined } })
                    if (val.length === 10) {
                        store = getPickupList.data?.find(s => s.phone === val)
                        if (store) {
                            dispatch({
                                type: 'update', payload: {
                                    pickupAddress: {
                                        lat: store.latitude, lng: store.longitude,
                                        address: store.address, pincode: store.pincode, phone: store.phone, placeId: store.googlePlaceId
                                    }, storeId: store.storeId
                                }
                            })
                        }
                    }
                }
            }} storePhone={state.storeContact} customerPhone={state.customerContact} onCustomerPhoneUpdate={val => {
                if (!val || /^\d{0,10}$/.test(val)) {
                    dispatch({ type: 'update', payload: { customerContact: val, dropLocation: val.length < 10 ? undefined : state.dropLocation } })
                    if (val.length === 10) {
                        getCustomerInfo.mutate(val)
                    }
                }

            }} onAddressClick={type => {
                dispatch({ type: 'update', payload: { searchLocation: true, addressType: type } })
                navigate({
                    to: '/paytm/neworder/location', search: {
                        selectedAddress: type === 'store' ? state.pickupAddress : state.dropLocation,
                        storeId: state.storeId,
                        addressType: type,
                        phone: type === 'store' ? state.storeContact : state.customerContact
                    }
                })
            }} pickupAddress={state.pickupAddress} dropAddress={state.dropLocation} />
            <div className='absolute bottom-1 left-0 right-0 flex justify-center items-center px-3'>
            <Button title="Find Delivery Partner" onClick={() => {
                    if (state.pickupAddress && state.dropLocation) {
                        getPriceQuotes.mutate({
                            storeDetails: {
                                lat: state.pickupAddress.lat, lng: state.pickupAddress.lng,
                                pin: state.pickupAddress.pincode, city: state.pickupAddress.address.city
                            },
                            drop: {
                                lat: state.dropLocation.lat, lng: state.dropLocation.lng,
                                pin: state.dropLocation.pincode
                            }
                        })
                    }
                }} disabled={!state.dropLocation || !state.pickupAddress}/>
            </div>
        </div>
        {getPriceQuotes.isPending?<LoadingDeliveryOptions />:null}
        <Outlet />
    </div>
}
